import React, { useState, useEffect } from 'react';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';


import { Card, Col, Form, Row, Table } from 'react-bootstrap';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import SalesByCategoryChart from './comparativo-ventas/SalesByCategoryChart';


const SalesByCategory = () => {

    const { token, setToken } = useToken();
    const { showErrorToast } = useToast();


    const [data, setData] = useState([]);
    const [selectedPeriod, setSelectedPeriod] = useState('current');
    const [selectedSeller, setSelectedSeller] = useState('all');
    const [sellers, setSellers] = useState([]);
    const [cargandoData, setCargandoData] = useState(false);

    // Fetch inicial de vendedores
    useEffect(() => {
        obtenerVendedores();
    }, []);

    // Fetch de datos cuando cambian los filtros
    useEffect(() => {
        obtenerVentasCategorias();
    }, [selectedPeriod, selectedSeller]);


    /**
     * Obtiene las ventas totales anuales por mes
     */
    const obtenerVendedores = async () => {
        setCargandoData(true);

        try {
            const response = await fetch("/api/Administradores/getVendedores", {
                method: 'POST',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setToken(null);
                    location.href = "";
                }
                throw new Error(`${response.status} ${response.statusText}`);
            }

            const responseType = await response.json();


            if (responseType.iResponseType === 1) {
                const detalles = JSON.parse(responseType.detalles);
                const vendedores = detalles.table2;

                const options = vendedores
                    .filter(v => v.vUsuario !== 'UsuarioPorDefecto')
                    .map(v => ({
                        value: v.idUsuario,
                        label: v.vNombre
                    }));

                // Agregar opción "Todos los vendedores"
                setSellers([{ value: 'all', label: 'Todos los vendedores' }, ...options]);
            
            
            } else {
                showErrorToast(responseType.vMessage);
            }
        } catch (error) {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error.message);
            console.error('❌ Error en obtenerVendedores:', error);
        }

        setCargandoData(false);
    };


    /**
     * Obtiene las ventas totales anuales por mes
     */
    const obtenerVentasCategorias = async () => {
        setCargandoData(true);

        const body = {
            fechaInicio: getFechaInicio(selectedPeriod),
            fechaFin: getFechaFin(selectedPeriod),
            idVendedor: isNaN(parseInt(selectedSeller)) ? null : parseInt(selectedSeller)
        };

        const response = await fetch("/api/Administradores/obtenerVentasPorCategorias", {
            method: 'POST',
            headers: {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        }).catch((error) => {
            showErrorToast('Hubo un problema con la petición Fetch: ' + error);
        });

        if (response?.ok) {
            const responseType = await response.json();

            if (responseType.iResponseType === 1) {
                const parsed = JSON.parse(responseType.detalles);
                const dataArray = parsed.table2;

                const totalGlobal = dataArray.reduce((sum, item) => sum + item.totalActual, 0);

                const dataMapped = dataArray.map(item => {
                    const variacionPorcentual = item.totalPrevio === 0
                        ? 0
                        : ((item.totalActual - item.totalPrevio) / item.totalPrevio) * 100;

                    return {
                        label: item.categoria,
                        value: (totalGlobal === 0
                            ? '0.0%'
                            : ((item.totalActual / totalGlobal) * 100).toFixed(1) + '%'),
                        progress: variacionPorcentual >= 0,
                        caret: variacionPorcentual >= 0
                            ? 'fas fa-caret-up text-success'
                            : 'fas fa-caret-down text-danger',
                        color: getColorByCategory(item.categoria),
                        progressValue: Math.abs(variacionPorcentual).toFixed(1) + '%'
                    };
                });


                setData(dataMapped);

            } else {
                showErrorToast(responseType.vMessage);
            }
        } else {
            showErrorToast(response?.status + ' ' + response?.statusText);

            if (response?.status === 401) {
                setToken(null);
                location.href = "";
            }
        }

        setCargandoData(false);
    };

    function getFechaInicio(period) {
        const today = new Date();
        const year = today.getFullYear();

        if (period === 'current' || period === 'AÑO ACTUAL') {
            return `${year}-01-01`;
        } else if (period === 'last' || period === 'AÑO PASADO') {
            return `${year - 1}-01-01`;
        } else {
            // Por si llega "2023", "2022", etc.
            return `${parseInt(period)}-01-01`;
        }
    }

    function getFechaFin(period) {
        const today = new Date();
        const year = today.getFullYear();

        if (period === 'current' || period === 'AÑO ACTUAL') {
            return today.toISOString().split("T")[0]; // Fecha actual
        } else if (period === 'last' || period === 'AÑO PASADO') {
            return `${year - 1}-12-31`;
        } else {
            return `${parseInt(period)}-12-31`;
        }
    }

 

    const getColorByCategory = (categoria) => {
        switch (categoria) {
            case 'Venta Nueva':
                return 'primary';
            case 'Recompra':
                return 'success';
            case 'Distribución':
                return 'info';
            default:
                return 'secondary'; // fallback
        }
    };
    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
    };

    const handleSellerChange = (seller) => {
        setSelectedSeller(seller);
    };
    return (
        <Card className="h-100">
            <FalconCardHeader
                title="Ventas por categorías"
                titleTag="h6"
                className="py-2"
                light
            />
            <Card.Body
                as={Flex}
                direction="column"
                justifyContent="between"
                className="py-0"
            >
                <div className="my-auto py-5 py-md-5">
                    {cargandoData ? (
                        <div className="text-center">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                        </div>
                    ) : (
                        <SalesByCategoryChart data={data} />
                    )}
                </div>
                <div className="border-top">
                    <Table size="sm" className="mb-0">
                        <tbody>
                            {cargandoData ? (
                                <tr>
                                    <td colSpan="3" className="text-center py-3">
                                        <div className="spinner-border spinner-border-sm text-primary" role="status">
                                            <span className="visually-hidden">Cargando...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data.map(item => (
                                    <tr key={item.label}>
                                        <td className="py-2">
                                            <Flex alignItems="center">
                                                {item.icon && <img src={item.icon} alt={item.label} width="16" height="16" className="me-2" />}
                                                <span className="text-900 fw-medium">{item.label}</span>
                                            </Flex>
                                        </td>
                                        <td className="py-2 text-end">
                                            <span className="text-900 fw-bold">{item.value}</span>
                                        </td>
                                        <td className="py-2 text-end">
                                            <Flex alignItems="center" justifyContent="end">
                                                <i className={item.caret} />
                                                <span
                                                    className={`ms-1 fw-semi-bold ${item.progress ? 'text-success' : 'text-danger'
                                                        }`}
                                                >
                                                    {item.progressValue}
                                                </span>
                                            </Flex>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
            <Card.Footer className="bg-light py-2">
                <Row className="g-0 flex-between-center">
                    <Col xs="auto" className="d-flex gap-2">
                        <Form.Select
                            size="sm"
                            className="me-2"
                            value={selectedPeriod}
                            disabled={cargandoData}
                            onChange={(e) => handlePeriodChange(e.target.value)}
                        >
                            <option value="current">AÑO ACTUAL</option>
                            <option value="last">AÑO PASADO</option>
                            <option value="2023">2023</option>
                            <option value="2022">2022</option>
                            <option value="2021">2021</option>
                            <option value="2020">2020</option>
                        </Form.Select>
                        <Form.Select
                            size="sm"
                            value={selectedSeller}
                            onChange={(e) => handleSellerChange(e.target.value)}
                        >
                            {sellers.map(seller => (
                                <option key={seller.value} value={seller.value}>
                                    {seller.label}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
            </Card.Footer>
        </Card>
    );
};

export default SalesByCategory;